import { call, put, takeLatest, select } from 'redux-saga/effects';
import { get, sortBy } from 'lodash';
import { message } from 'antd';
import * as types from './types';
import * as actions from './actions';
import { ViewTypes } from './reducers';
import * as api from '@api/precision';
import { setOrder } from '@src/store/policies/schedule/actions';
import { formViewSelector } from '../schedule/selectors';

const displayError = (err) => {
  const error = get(err, 'response.data.message', 'Unknown Error');
  message.error(error);
};

function* init() {
  try {
    yield put(actions.toggleLoading(true));
    const resp = yield call(api.getPolicies);
    const { data } = resp;
    const { policies = [] } = data;
    const sortedPolices = sortBy(policies, (p) => p.index);
    const order = sortedPolices.map((p) => p.id);
    yield put(actions.setData(data));
    yield put(setOrder(order));
  } catch (err) {
    displayError(err);
  } finally {
    yield put(actions.toggleLoading(false));
  }
}

function* activate(action) {
  try {
    const resp = yield call(api.activatePolicy, action.id);
    const { data } = resp;
    yield put(actions.setData(data));
    message.success('New policy activated');
  } catch (err) {
    displayError(err);
  }
}

function* deletePolicy(action) {
  try {
    const resp = yield call(api.deletePolicy, action.id);
    const { data } = resp;
    yield put(actions.setData(data));
    message.success('Policy deleted!');
  } catch (err) {
    displayError(err);
  }
}

function* clone(action) {
  try {
    const resp = yield call(api.clonePolicy, action.id);
    const { data } = resp;
    yield put(actions.setData(data));
    message.success('Policy cloned!');
  } catch (err) {
    displayError(err);
  }
}

function* update(action) {
  try {
    yield put(actions.toggleSubmitting(true));
    const resp = yield call(api.updatePolicy, action.data);
    const { data } = resp;
    yield put(actions.setData(data));
    yield put(actions.toggleModal(false));
    message.success('Policy updated!');
  } catch (err) {
    displayError(err);
  } finally {
    yield put(actions.toggleSubmitting(false));
  }
}

function* reorder(action) {
  try {
    const { scheduleId } = yield select(formViewSelector);
    const resp = yield call(api.reorderPolicies, { order: action.order, scheduleId });
    const { data } = resp;
    yield put(actions.setData(data));
    yield put(actions.toggleSortable(false));
    message.success('Policy order updated!');
  } catch (err) {
    displayError(err);
  } finally {
    yield put(actions.toggleSubmitting(false));
  }
}

// reload policy data on view change
function* viewChange(action) {
  const { view } = action;
  if (view === ViewTypes.policies) {
    yield put(actions.init());
  }
}

const watchMap = {
  [types.INIT]: init,
  [types.ACTIVATE_POLICY]: activate,
  [types.DELETE_POLICY]: deletePolicy,
  [types.CLONE_POLICY]: clone,
  [types.UPDATE_POLICY]: update,
  [types.REORDER_POLICIES]: reorder,
  [types.SET_VIEW]: viewChange,
};
function* watch() {
  for (const [type, func] of Object.entries(watchMap)) {
    yield takeLatest([type], func);
  }
}

export default [watch];
